/**
 * Controls: DOM events -> ClientState (DESIGN 6.1). The left hand lives on
 * QWER/ASDF, the right hand on the mouse: LMB force-casts (Shift: area),
 * MMB self-casts (Shift+RMB / V: weapon), RMB walks toward the cursor,
 * arrows walk in eight directions, Space (or G) casts a magick,
 * Backspace clears. Tab (hold), Enter, M, O, Escape / P (the menu) and N
 * (ready) are UI intents that the screens subscribe to with `on()`.
 *
 * Key handling uses KeyboardEvent.code (physical position). Aim is sampled
 * once per frame in `update()` from the predicted local position to the
 * cursor's world position, so casts always carry the angle the HUD shows.
 */

import type { BaseElement, CastMode, Vec2 } from '@shared/index.js';
import { CLIENT, angleTo } from '@shared/index.js';
import type { ClientState } from '../state/clientState.js';
import { arrowDirection } from './bindings.js';
import { pressesFocusedControl, tabKeyRole } from './tabFocus.js';
import type { MoveKey } from './bindings.js';
import {
  DEFAULT_KEYMAP, ELEMENT_ACTIONS, HOLD_ACTIONS, cloneKeymap, indexKeymap, isModifierCode, keyBinding, lookup, mouseBinding,
  type BindAction, type Keymap,
} from './keymap.js';

function isElementAction(a: BindAction): a is BaseElement {
  return (ELEMENT_ACTIONS as readonly string[]).includes(a);
}

export type ControlsEvent = 'chat' | 'spellbook' | 'mute' | 'settings' | 'pause' | 'ready';
export type ControlsListener = (open: boolean) => void;

export class Controls {
  /** Cursor position in world units, updated in place every `update()`. */
  readonly cursorWorld: Vec2 = { x: 0, y: 0 };

  private enabled = true;
  private readonly heldMove = new Set<MoveKey>();
  private rmbHeld = false;
  /** RMB click target; persists after release until arrival, arrows or a new RMB (DESIGN 6.1). */
  private clickTarget: Vec2 | null = null;
  /** Inputs currently held down ("k:KeyA" / "m:0") and the hold action each started. */
  private readonly held = new Map<string, BindAction>();
  private virtualMove: Vec2 | null = null;
  private map: Keymap = cloneKeymap(DEFAULT_KEYMAP);
  private index = indexKeymap(this.map);
  private clientX = 0;
  private clientY = 0;
  private hasMouse = false;
  private spellbookOpen = false;
  private tabMovesFocus: () => boolean = () => false;
  private _muted = false;
  private _aim = 0;
  private readonly listeners = new Map<ControlsEvent, Set<ControlsListener>>();
  private readonly unbind: (() => void)[] = [];

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly state: ClientState,
    private readonly screenToWorld: (px: number, py: number) => Vec2,
  ) {
    this.listen(window, 'keydown', (ev: KeyboardEvent) => this.onKeyDown(ev));
    this.listen(window, 'keyup', (ev: KeyboardEvent) => this.onKeyUp(ev));
    this.listen(window, 'mousemove', (ev: MouseEvent) => this.onMouseMove(ev));
    this.listen(window, 'mouseup', (ev: MouseEvent) => this.onMouseUp(ev));
    this.listen(window, 'blur', () => this.releaseAll());
    this.listen(canvas, 'mousedown', (ev: MouseEvent) => this.onMouseDown(ev));
    // Whole page, not just the canvas: a right-click walk that ends over a side panel must not open
    // the browser menu (its focus steal would release every held key mid-fight).
    this.listen(window, 'contextmenu', (ev: Event) => ev.preventDefault());
  }

  /** Last aim angle pushed to the state (radians, y down). */
  get aim(): number { return this._aim; }
  /** Mute toggle state driven by M; the Sfx mirrors it through the "mute" event. */
  get muted(): boolean { return this._muted; }
  get isEnabled(): boolean { return this.enabled; }

  /**
   * Once per frame: refresh the cursor's world position, aim from the
   * predicted local position toward it, and push the movement intent.
   */
  update(_dtSeconds: number): void {
    if (this.hasMouse) {
      const r = this.canvas.getBoundingClientRect();
      const w = this.screenToWorld(this.clientX - r.left, this.clientY - r.top);
      this.cursorWorld.x = w.x;
      this.cursorWorld.y = w.y;
    }
    if (!this.enabled) return;

    const me = this.state.predictedPos();
    if (me) {
      this._aim = angleTo(me, this.cursorWorld);
      this.state.setAim(this._aim);
    }

    // Dying forgets the walk target, so a revived wizard does not stroll back to where they fell.
    if (this.state.viewModel.me?.dead) this.clickTarget = null;

    const dir = arrowDirection(this.heldMove);
    if (dir) {
      this.state.setMove({ k: 'd', x: dir.x, y: dir.y });
      return;
    }
    if (this.virtualMove) {
      this.state.setMove({ k: 'd', x: this.virtualMove.x, y: this.virtualMove.y });
      return;
    }
    if (this.rmbHeld) {
      this.clickTarget = { x: this.cursorWorld.x, y: this.cursorWorld.y };
    } else if (this.clickTarget && me && Math.hypot(me.x - this.clickTarget.x, me.y - this.clickTarget.y) <= CLIENT.rmbStopDistance) {
      // Arrived: forget the target so a later knockback does not walk us back.
      this.clickTarget = null;
    }
    if (this.clickTarget) this.state.setMove({ k: 't', x: this.clickTarget.x, y: this.clickTarget.y });
    else this.state.setMove({ k: 's' });
  }

  /** false while chat or the landing page has focus: keys go to the DOM, held inputs are released. */
  setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    if (!enabled) {
      this.releaseAll();
      this.clickTarget = null;
      this.state.setMove({ k: 's' });
    }
  }

  /** Asked on a fresh Tab press: true leaves it to the browser (menus), false makes it the spellbook key (input/tabFocus.ts). */
  setTabMovesFocus(fn: () => boolean): void {
    this.tabMovesFocus = fn;
  }

  on(evt: ControlsEvent, fn: ControlsListener): () => void {
    let set = this.listeners.get(evt);
    if (!set) {
      set = new Set();
      this.listeners.set(evt, set);
    }
    set.add(fn);
    return () => { set.delete(fn); };
  }

  dispose(): void {
    for (const off of this.unbind) off();
    this.unbind.length = 0;
    this.listeners.clear();
  }

  // ------------------------------------------------------------------ virtual input (touch.ts)

  /** Touch: aim at a screen point (CSS px, like a mouse cursor). */
  setPointerScreen(clientX: number, clientY: number): void {
    this.clientX = clientX;
    this.clientY = clientY;
    this.hasMouse = true;
  }

  /** Touch: press an action as if a bound key went down; `source` pairs it with releaseVirtual. */
  pressVirtual(action: BindAction, source: string): void {
    if (!this.enabled) return;
    this.press(action, `v:${source}`);
  }

  releaseVirtual(source: string): void {
    this.releaseSource(`v:${source}`);
  }

  /** Touch joystick: a direction of length <= 1 (shorter walks slower), or null to stop. */
  setVirtualMove(dir: Vec2 | null): void {
    this.virtualMove = dir;
  }

  // ------------------------------------------------------------------ input -> action (keymap.ts)

  /** Current bindings; replace with setKeymap (settings screen). */
  get keymap(): Readonly<Keymap> { return this.map; }

  setKeymap(map: Keymap): void {
    this.releaseAll();
    this.map = map;
    this.index = indexKeymap(map);
  }

  private onKeyDown(ev: KeyboardEvent): void {
    if (!this.enabled) return;
    // Browser chords (Ctrl+R, Alt+Tab, Cmd+Q...) are never game input.
    if (ev.ctrlKey || ev.metaKey || ev.altKey) return;
    if (isModifierCode(ev.code)) return;
    if (pressesFocusedControl(ev)) return;
    const action = lookup(this.index, keyBinding(ev.code, ev.shiftKey));
    if (ev.code === 'Tab') {
      // In a menu the browser walks the controls; mid-match Tab is the spellbook and focus never moves.
      const role = tabKeyRole(action, this.held.has('k:Tab'), this.tabMovesFocus());
      if (role === 'pass') return;
      if (role === 'swallow') {
        ev.preventDefault();
        return;
      }
    }
    if (!action) return;
    ev.preventDefault();
    if (ev.repeat) return;
    this.press(action, `k:${ev.code}`);
  }

  /** Releases are honoured even while disabled so nothing stays "held" across a chat box. */
  private onKeyUp(ev: KeyboardEvent): void {
    this.releaseSource(`k:${ev.code}`);
  }

  private onMouseMove(ev: MouseEvent): void {
    this.clientX = ev.clientX;
    this.clientY = ev.clientY;
    this.hasMouse = true;
  }

  private onMouseDown(ev: MouseEvent): void {
    if (!this.enabled) return;
    const action = lookup(this.index, mouseBinding(ev.button, ev.shiftKey));
    if (!action) return;
    ev.preventDefault(); // no middle-click autoscroll, no text selection
    this.press(action, `m:${ev.button}`);
  }

  private onMouseUp(ev: MouseEvent): void {
    this.releaseSource(`m:${ev.button}`);
  }

  /** A physical input went down and maps to `action`; `source` identifies it for the matching release. */
  private press(action: BindAction, source: string): void {
    if (HOLD_ACTIONS.has(action)) this.held.set(source, action);
    if (isElementAction(action)) {
      this.state.queueAction({ a: 'el', e: action });
      return;
    }
    switch (action) {
      case 'force': this.cast('force', 'b'); return;
      case 'area': this.cast('area', 'b'); return;
      case 'self': this.cast('self', 'b'); return;
      case 'weapon': this.weaponPress(); return;
      case 'magick': this.state.queueAction({ a: 'magick' }); return;
      case 'clear': this.state.queueAction({ a: 'clear' }); return;
      case 'weave': this.state.queueAction({ a: 'weave' }); return;
      case 'stash1': this.state.queueAction({ a: 'stash', s: 0 }); return;
      case 'stash2': this.state.queueAction({ a: 'stash', s: 1 }); return;
      case 'walk':
        this.rmbHeld = true;
        this.clickTarget = { x: this.cursorWorld.x, y: this.cursorWorld.y };
        return;
      case 'moveUp': case 'moveDown': case 'moveLeft': case 'moveRight':
        this.heldMove.add(action);
        this.clickTarget = null;
        return;
      case 'spellbook':
        if (!this.spellbookOpen) { this.spellbookOpen = true; this.emit('spellbook', true); }
        return;
      case 'chat': this.emit('chat', true); return;
      case 'mute':
        this._muted = !this._muted;
        this.emit('mute', this._muted);
        return;
      case 'settings': this.emit('settings', true); return;
      case 'pause': this.emit('pause', true); return;
      case 'ready': this.emit('ready', true); return;
    }
  }

  private releaseSource(source: string): void {
    const action = this.held.get(source);
    if (!action) return;
    this.held.delete(source);
    this.release(action);
  }

  private release(action: BindAction): void {
    switch (action) {
      case 'force': this.cast('force', 'e'); return;
      case 'walk':
        // Hold to walk, release to stop (Magicka feel): no lingering target.
        this.rmbHeld = false;
        this.clickTarget = null;
        return;
      case 'moveUp': case 'moveDown': case 'moveLeft': case 'moveRight':
        this.heldMove.delete(action);
        return;
      case 'spellbook':
        if (this.spellbookOpen) { this.spellbookOpen = false; this.emit('spellbook', false); }
        return;
      default: return;
    }
  }

  // ------------------------------------------------------------------ helpers

  private cast(m: CastMode, ph: 'b' | 'e'): void {
    this.state.queueAction({ a: 'cast', m, ph, aim: this._aim });
  }

  /**
   * V / Shift+MMB: with a queue the press imbues the weapon (a weapon-mode
   * cast consumes the queue); with an empty queue it swings whatever is
   * imbued, or a plain staff (DESIGN 6.1). The predicted queue decides, so
   * the second press after an imbue is already a swing.
   */
  private weaponPress(): void {
    if (this.state.queue.length > 0) this.cast('weapon', 'b');
    else this.state.queueAction({ a: 'melee', aim: this._aim });
  }

  private releaseAll(): void {
    this.virtualMove = null;
    for (const action of this.held.values()) this.release(action);
    this.held.clear();
    this.heldMove.clear();
    this.rmbHeld = false;
    if (this.spellbookOpen) {
      this.spellbookOpen = false;
      this.emit('spellbook', false);
    }
  }

  private emit(evt: ControlsEvent, open: boolean): void {
    const set = this.listeners.get(evt);
    if (set) for (const fn of set) fn(open);
  }

  private listen<E extends Event>(target: EventTarget, type: string, fn: (ev: E) => void): void {
    const handler = fn as unknown as EventListener;
    target.addEventListener(type, handler);
    this.unbind.push(() => target.removeEventListener(type, handler));
  }
}
